import { convertPrice } from "../services/currencyService";
import {
  formatPackageMarketingTextPriceIls,
  resolvePackageMarketingTextDisplayPrice,
} from "./resolvePackageMarketingTextDisplayPrice";
import { get_depret_from_result } from "./LDSHelpers";
import translateLDSDestinations from "../services/translateLDSDestinations";
import { getUserInfo } from "../services/localStorage";
import { isDealTransferIncluded } from "./isDealTransferIncluded";
import { buildVpPdfPackageInclusions } from "./buildVpPdfPackageInclusions";

/**
 * שורת כבודה/טרולי כלולים במחיר החבילה — אותו SSOT כמו PDF / sidebar.
 * @param {object|null|undefined} selectedResult
 * @returns {string} שורה להעתקה, או מחרוזת ריקה אם אין כבודה כלולה
 */
export function formatVacationPackageIncludedBaggageMarketingLine(selectedResult) {
  const inclusions = buildVpPdfPackageInclusions({ selectedResult });
  if (!inclusions?.baggageIncluded) return "";
  const details = String(inclusions.baggageSummaryText || "").trim();
  if (details) {
    return `🧳 ${details} — כלול במחיר`;
  }
  return "🧳 כבודה כלולה במחיר";
}

/**
 * Generate marketing text for packages
 */
export const generatePackageMarketingText = ({
  selectedResult,
  hotel,
  roomsAndPaxInfo,
  ourPrice,
  packageProfileDisplayOurPrice = null,
  billableAttractionsIls = 0,
  persistedPublicOfferIls = null,
  ilsToUsd = null,
  ourSavedLDSOrder,
  agentData,
  airlines,
  t,
  ldsSupplement,
}) => {
  if (!selectedResult || !hotel) {
    return null;
  }

  try {
    // Get hotel information
    const hotelName = hotel?.hotelName || "מלון";
    const starRating = hotel?.category ? hotel.category.replace(/\D/g, "") : null;
    const starsDisplay = starRating ? "⭐".repeat(parseInt(starRating)) : "";

    // Get dates
    const { depDate, retDate } = get_depret_from_result(selectedResult);

    // Get hotel city
    const hotelCityCode = hotel?.hotelCity || "";
    const hotelCityTranslated = translateLDSDestinations(hotelCityCode);
    const hotelCity = hotelCityTranslated?.CITYNAME_HEB || hotelCityCode;

    // Get board basis
    const getBoardDetails = (board) => {
      if (!board) return "";
      const boardCode = typeof board === "string" ? board.split(" ").pop() : board;
      switch (boardCode) {
        case "BB":
          return t("boards.BB");
        case "HB":
          return t("boards.HB");
        case "FB":
          return t("boards.FB");
        case "AI":
          return t("boards.AI");
        case "RO":
          return t("boards.RO");
        default:
          return boardCode;
      }
    };
    const boardBasis = hotel?.board || "";
    const boardDetails = getBoardDetails(boardBasis);

    // Get number of nights
    const nights = hotel?.nights || "";

    const orderId =
      ourSavedLDSOrder?.id ??
      ourSavedLDSOrder?.order_id ??
      selectedResult?.orderId ??
      null;
    const packageId =
      selectedResult?.dealId ??
      selectedResult?.ldsDealId ??
      selectedResult?.fullDeal?.dealId ??
      null;

    const { priceStr } = resolvePackageMarketingTextDisplayPrice({
      ourPrice,
      packageProfileDisplayOurPrice,
      billableAttractionsIls,
      persistedPublicOfferIls,
      ilsToUsd,
      orderId,
      packageId,
    });

    // Get link if available
    const packageLink = ourSavedLDSOrder ? window.location.href : "";

    // Get agent information
    const userInfoFromStorage = getUserInfo();
    const agent = agentData || ourSavedLDSOrder?.agent || userInfoFromStorage || null;

    let agentName = "";
    if (agent) {
      const firstName = agent.firstname || agent.firstName || agent.FirstName || "";
      const lastName = agent.lastname || agent.lastName || agent.LastName || "";
      agentName = `${firstName} ${lastName}`.trim();
      if (!agentName) {
        agentName = agent.name || agent.Name || "";
      }
    }

    const agentPhone =
      agent?.cell ||
      agent?.Cell ||
      agent?.phone ||
      agent?.Phone ||
      agent?.mobile ||
      agent?.Mobile ||
      agent?.telephone ||
      agent?.Telephone ||
      "";

    // Get category/subcategory
    const getCategoryLabel = (categoryKey) => {
      if (!categoryKey || categoryKey === "ALL") return null;
      const categoryMap = {
        "ADULTS ONLY": "מבוגרים בלבד",
        Attractions: "אטרקציות",
        Cruise: "קרוז",
        Football: "כדורגל",
        "HOTEL AND TOUR": "מלון וטיול",
        Kosher: "כשר",
        MASSORET: "מסורת",
        Russian: "רוסית",
        "SHOP AND STORE": "קניות",
        "SKI KOSHER": "סקי כשר",
        "SPA MEDICAL": "ספא רפואי",
        SHOW: "הופעות",
        Ski: "סקי",
        Spa: "ספא",
        Sport: "ספורט",
        "Water Park": "פארק מים",
        fashionTour: "טיול אופנה",
      };
      return categoryMap[categoryKey] || categoryKey;
    };

    const category =
      selectedResult?.searchPhrase?.categories?.[0] ||
      selectedResult?.searchPhrase?.category ||
      selectedResult?.category ||
      selectedResult?.subject ||
      null;
    const categoryLabel = getCategoryLabel(category);

    // Build marketing text
    let marketingText = categoryLabel
      ? `🎁 *חבילת ${categoryLabel} מיוחדת!*\n\n`
      : `🎁 *חבילת נופש מיוחדת!*\n\n`;
    marketingText += `🏨 *${hotelName}${starsDisplay ? ` ${starsDisplay}` : ""}*\n`;
    marketingText += `${hotelCity}\n`;
    if (boardDetails) {
      marketingText += `בסיס אירוח: ${boardDetails}\n`;
    }
    if (nights) {
      marketingText += `${nights} לילות\n`;
    }
    if (depDate && retDate) {
      marketingText += `${depDate} - ${retDate}\n`;
    }

    // Add transfers if included
    if (isDealTransferIncluded(selectedResult)) {
      marketingText += `🚌 כולל העברות\n`;
    }

    // Included trolley / checked bag from the fare (not paid extras)
    const includedBaggageLine =
      formatVacationPackageIncludedBaggageMarketingLine(selectedResult);
    if (includedBaggageLine) {
      marketingText += `${includedBaggageLine}\n`;
    }

    // Extract relevant information from clientRemarks
    const extractRelevantInfo = (html) => {
      if (!html) return null;
      const text = html
        .replace(/<[^>]*>/g, " ")
        .replace(/&[a-z]+;/gi, " ")
        .replace(/\s+/g, " ")
        .trim();
      if (!text) return null;

      const patterns = {
        showName: /(?:הופעה|קונצרט|מופע|אירוע)[:\s]+([^.\n]+)/i,
        showDate: /(?:תאריך|תאריך הופעה|תאריך האירוע)[:\s]+([^.\n]+)/i,
        showVenue: /(?:אולם|מקום|מיקום|ארנה)[:\s]+([^.\n]+)/i,
        eventInfo: /(?:כולל|כוללת)[:\s]+([^.\n]+)/i,
      };

      const extracted = {};
      const showMatch = text.match(patterns.showName);
      if (showMatch && showMatch[1]) {
        extracted.showName = showMatch[1].trim();
      }
      const dateMatch = text.match(patterns.showDate);
      if (dateMatch && dateMatch[1]) {
        extracted.showDate = dateMatch[1].trim();
      }
      const venueMatch = text.match(patterns.showVenue);
      if (venueMatch && venueMatch[1]) {
        extracted.venue = venueMatch[1].trim();
      }
      if (Object.keys(extracted).length > 0) {
        return extracted;
      }

      const showKeywords = ["הופעה", "קונצרט", "מופע", "אירוע", "SHOW", "CONCERT", "EVENT"];
      const hasShowInfo = showKeywords.some((keyword) =>
        text.toLowerCase().includes(keyword.toLowerCase())
      );
      if (hasShowInfo) {
        const firstSentence = text.split(/[.!?]/)[0].trim();
        if (firstSentence && firstSentence.length > 10 && firstSentence.length < 150) {
          return { summary: firstSentence };
        }
      }
      return null;
    };

    const relevantInfo = extractRelevantInfo(selectedResult?.clientRemarks);
    if (relevantInfo) {
      marketingText += `\n🎭 *מידע נוסף:*\n`;
      if (relevantInfo.showName) {
        marketingText += `הופעה: ${relevantInfo.showName}\n`;
      }
      if (relevantInfo.showDate) {
        marketingText += `תאריך: ${relevantInfo.showDate}\n`;
      }
      if (relevantInfo.venue) {
        marketingText += `מקום: ${relevantInfo.venue}\n`;
      }
      if (relevantInfo.summary) {
        marketingText += `${relevantInfo.summary}\n`;
      }
    }

    // Get flight information
    const flightsData =
      selectedResult?.linkedFlights?.flight || selectedResult?.linkedFlights?.flight;
    if (flightsData && airlines) {
      const flightsArray = Array.isArray(flightsData) ? flightsData : [flightsData];
      if (flightsArray.length > 0) {
        marketingText += `\n✈️ *פרטי טיסות:*\n`;
        flightsArray.forEach((flightItem, idx) => {
          const leg = Array.isArray(flightItem?.leg) ? flightItem.leg[0] : flightItem?.leg;
          if (leg) {
            const flightType = idx === 0 ? "טיסת הלוך" : "טיסת חזרה";
            const airlineId =
              leg.airlineId || leg.airline?.id || leg.airline?.code || leg.airlineCode || "";
            let airline = null;
            if (airlineId && airlines) {
              airline = airlines.find(
                (a) =>
                  a.id?.toString() === airlineId.toString() ||
                  a.code?.toLowerCase() === airlineId.toString().toLowerCase()
              );
            }
            if (!airline && leg.flightNumber && airlines) {
              const flightNumberCode = leg.flightNumber.substring(0, 2).toUpperCase();
              airline = airlines.find((a) => a.code?.toUpperCase() === flightNumberCode);
            }
            const airlineCode = airline?.code || airlineId || "";
            const airlineName = airline?.name || airlineCode;
            const flightNumber = leg.flightNumber || "";
            const depTime = leg.depTime || "";
            const arrTime = leg.arrTime || "";
            const availableSeats =
              leg.availableSeats || leg.seatsAvailable || leg.seats || "";

            const depCityCode = leg.depCity || leg.depAirport?.code || "";
            const arrCityCode = leg.arrCity || leg.arrAirport?.code || "";
            const depCityTranslated = translateLDSDestinations(depCityCode);
            const arrCityTranslated = translateLDSDestinations(arrCityCode);
            const depCityName = depCityTranslated?.CITYNAME_HEB || depCityCode;
            const arrCityName = arrCityTranslated?.CITYNAME_HEB || arrCityCode;

            marketingText += `\n*${flightType}:*\n`;
            marketingText += `${depCityName} → ${arrCityName}\n`;
            if (airlineName) {
              marketingText += `חברת תעופה: ${airlineName}\n`;
            }
            if (flightNumber) {
              marketingText += `מספר טיסה: ${airlineCode} ${flightNumber}\n`;
            }
            if (depTime) {
              marketingText += `המראה: ${depTime}\n`;
            }
            if (arrTime) {
              marketingText += `נחיתה: ${arrTime}\n`;
            }
            if (availableSeats) {
              marketingText += `מקומות זמינים: ${availableSeats}\n`;
            }
          }
        });
      }
    }

    // Get room information
    if (roomsAndPaxInfo && roomsAndPaxInfo.length > 0) {
      marketingText += `\n🛏️ *חדרים:*\n`;
      roomsAndPaxInfo.forEach((roomInfo, index) => {
        const room = roomInfo?.room;
        const roomName = room?.roomType || room?.roomSet || `חדר ${index + 1}`;
        const adultsCount = roomInfo?.adults?.length || 0;
        const kidsCount = roomInfo?.kids?.length || 0;
        const infantsCount = roomInfo?.infants?.length || 0;

        marketingText += `\n*חדר ${index + 1}:* ${roomName}\n`;

        let occupancy = "";
        if (adultsCount > 0) {
          occupancy = `${adultsCount} מבוגרים`;
          if (kidsCount > 0) {
            occupancy += `, ${kidsCount} ילדים`;
          }
          if (infantsCount > 0) {
            occupancy += `, ${infantsCount} תינוקות`;
          }
        }
        if (occupancy) {
          marketingText += `${occupancy}\n`;
        }
      });
    }

    // Get luggage information if any supplements are checked
    if (ldsSupplement && typeof ldsSupplement === 'object') {
      const checkedSupplements = Object.values(ldsSupplement).filter(
        (supp) => supp?.checked && (supp?.type === 'LUGGAGE' || supp?.type === 'TROLLEY')
      );
      
      if (checkedSupplements.length > 0) {
        marketingText += `\n🧳 *מזוודות נבחרות:*\n`;
        
        // Group by passenger name for better readability
        const supplementsByPassenger = {};
        checkedSupplements.forEach((supp) => {
          const passengerName = supp?.pax 
            ? `${supp.pax.firstname || ''} ${supp.pax.lastname || ''}`.trim()
            : 'נוסע';
          
          if (!supplementsByPassenger[passengerName]) {
            supplementsByPassenger[passengerName] = [];
          }
          supplementsByPassenger[passengerName].push(supp);
        });
        
        Object.entries(supplementsByPassenger).forEach(([passengerName, supps]) => {
          marketingText += `\n*${passengerName}:*\n`;
          supps.forEach((supp) => {
            const description = supp?.description || '';
            const weightText = supp?.weightText || '';
            const flightInfo = supp?.flight?.fltRef === "-2" 
              ? "טיסה הלוך וחזור" 
              : supp?.flight?.fltRef === "-3"
              ? "כל הטיסות"
              : supp?.flight?.depCity && supp?.flight?.arrCity
              ? `${supp.flight.depCity} → ${supp.flight.arrCity}`
              : '';
            
            // Translate description
            let translatedDesc = description;
            translatedDesc = translatedDesc.replace(/BAGGAGE/gi, "מזוודה");
            translatedDesc = translatedDesc.replace(/baggage/gi, "מזוודה");
            translatedDesc = translatedDesc.replace(/TROLLEY/gi, "כבודת יד");
            translatedDesc = translatedDesc.replace(/trolley/gi, "כבודת יד");
            translatedDesc = translatedDesc.replace(/\bLDS\b/gi, "").trim();
            translatedDesc = translatedDesc.replace(/\b1\s*PCS\b/gi, "");
            translatedDesc = translatedDesc.replace(/(\d+)PCS/gi, (match, num) => {
              const count = parseInt(num);
              return count > 1 ? `${count} יחידות` : "";
            });
            translatedDesc = translatedDesc.replace(/\s+/g, " ").trim();
            
            if (translatedDesc || weightText) {
              marketingText += `• ${translatedDesc || 'מזוודה'}${weightText ? ` ${weightText}` : ''}${flightInfo ? ` (${flightInfo})` : ''}\n`;
            }
          });
        });
      }
    }

    marketingText += `\n💰 *מחיר: ${priceStr}*\n`;

    // Add payment options
    marketingText += `\n💳 *אפשרויות תשלום:*\n`;
    marketingText += `• עד 3 תשלומים ללא ריבית\n`;
    marketingText += `• עד 12 תשלומים\n`;
    marketingText += `• ניתן לשלם בהעברה בנקאית\n`;

    if (packageLink) {
      marketingText += `\n🔗 קישור להזמנה:\n${packageLink}\n`;
    }
    marketingText += `\n⭐ הזדמנות שלא תחזור!\n`;

    // Add agent information
    marketingText += `\n👤 *לפרטים נוספים:*\n`;
    if (agentName) {
      marketingText += `${agentName}\n`;
    }
    if (agentPhone) {
      marketingText += `${agentPhone}\n`;
    }
    if (!agentName && !agentPhone) {
      marketingText += `צרו קשר איתי\n`;
    }

    return marketingText;
  } catch (error) {
    console.error("Error generating marketing text:", error);
    return null;
  }
};

/**
 * Generate marketing text for hotels
 */
export const generateHotelMarketingText = ({
  hotelInfo,
  roomsAndPaxInfo,
  fullPrice,
  dates,
  numberOfNights,
  totalRooms,
  boardTypesDisplay,
  ourSavedLDSOrder,
  agentData,
  hotelDescription,
  facilitiesInfo,
  checkInOutInfo,
  t,
  translateFacility,
  i18n,
}) => {
  if (!hotelInfo) {
    return null;
  }

  try {
    const hotelName = hotelInfo?.HotelName || "מלון";
    const starRating = hotelInfo?.Category ? hotelInfo.Category.replace(/\D/g, "") : null;
    const starsDisplay = starRating ? "⭐".repeat(parseInt(starRating)) : "";
    const rating = hotelInfo?.Rating || hotelInfo?.rating || "";

    const hotelCityCode = hotelInfo?.HotelCity || "";
    const hotelCityTranslated = translateLDSDestinations(hotelCityCode);
    const hotelCity = hotelCityTranslated?.CITYNAME_HEB || hotelCityCode;
    const address = hotelInfo?.Address || "";

    // Get board basis
    const getBoardDetails = (board) => {
      if (!board) return "";
      const boardCode = typeof board === "string" ? board.split(" ").pop() : board;
      switch (boardCode) {
        case "BB":
          return t("boards.BB");
        case "HB":
          return t("boards.HB");
        case "FB":
          return t("boards.FB");
        case "AI":
          return t("boards.AI");
        case "RO":
          return t("boards.RO");
        default:
          return boardCode;
      }
    };

    const priceStr = convertPrice(fullPrice || 0);
    const hotelLink = ourSavedLDSOrder ? window.location.href : "";

    // Get agent information
    const userInfoFromStorage = getUserInfo();
    const agent = agentData || ourSavedLDSOrder?.agent || userInfoFromStorage || null;

    let agentName = "";
    if (agent) {
      const firstName = agent.firstname || agent.firstName || agent.FirstName || "";
      const lastName = agent.lastname || agent.lastName || agent.LastName || "";
      agentName = `${firstName} ${lastName}`.trim();
      if (!agentName) {
        agentName = agent.name || agent.Name || "";
      }
    }

    const agentPhone =
      agent?.cell ||
      agent?.Cell ||
      agent?.phone ||
      agent?.Phone ||
      agent?.mobile ||
      agent?.Mobile ||
      agent?.telephone ||
      agent?.Telephone ||
      "";

    // Build marketing text - always start with basic info
    let marketingText = `🏨 *${hotelName}${starsDisplay ? ` ${starsDisplay}` : ""}*\n\n`;
    
    // Add rating if available
    if (rating) {
      marketingText += `⭐ דירוג: ${rating}/10\n`;
    }
    
    // Add address
    if (address) {
      marketingText += `📍 ${address}\n`;
    }
    
    if (hotelCity) {
      marketingText += `🌍 ${hotelCity}\n`;
    }
    
    // If we have minimal info, at least add what we have
    if (!address && !hotelCity && !rating) {
      // Still add basic structure even with minimal data
    }
    
    marketingText += `\n`;
    
    // Add hotel description if available
    if (hotelDescription) {
      // Remove HTML tags and clean text
      let descriptionText = "";
      if (typeof hotelDescription === "string") {
        descriptionText = hotelDescription
          .replace(/<[^>]*>/g, " ")
          .replace(/&[a-z]+;/gi, " ")
          .replace(/\s+/g, " ")
          .trim();
      } else if (hotelDescription?.description) {
        descriptionText = hotelDescription.description
          .replace(/<[^>]*>/g, " ")
          .replace(/&[a-z]+;/gi, " ")
          .replace(/\s+/g, " ")
          .trim();
      }
      
      if (descriptionText && descriptionText.length > 0) {
        // Limit description to first 500 characters
        const shortDescription = descriptionText.length > 500 
          ? descriptionText.substring(0, 500) + "..." 
          : descriptionText;
        marketingText += `📝 *תיאור המלון:*\n${shortDescription}\n\n`;
      }
    }
    
    // Add facilities/amenities
    if (facilitiesInfo && facilitiesInfo.length > 0) {
      marketingText += `✨ *שירותים ואמצעים:*\n`;
      const facilitiesToShow = facilitiesInfo.slice(0, 15); // Show first 15 facilities
      facilitiesToShow.forEach((facility, index) => {
        const facilityName = facility?.facilityname || facility?.name || facility;
        let translatedName = facilityName;
        if (translateFacility && i18n) {
          translatedName = translateFacility(facilityName, i18n.language) || facilityName;
        }
        marketingText += `• ${translatedName}\n`;
      });
      if (facilitiesInfo.length > 15) {
        marketingText += `... ועוד ${facilitiesInfo.length - 15} שירותים נוספים\n`;
      }
      marketingText += `\n`;
    }
    
    // Add check-in/check-out info
    if (checkInOutInfo) {
      if (checkInOutInfo.checkIn || checkInOutInfo.checkInText) {
        marketingText += `🕐 *צ'ק אין:*\n`;
        if (checkInOutInfo.checkIn) {
          marketingText += `${checkInOutInfo.checkIn}\n`;
        }
        if (checkInOutInfo.checkInText) {
          marketingText += `${checkInOutInfo.checkInText}\n`;
        }
        marketingText += `\n`;
      }
      if (checkInOutInfo.checkOut || checkInOutInfo.checkOutText) {
        marketingText += `🕐 *צ'ק אאוט:*\n`;
        if (checkInOutInfo.checkOut) {
          marketingText += `${checkInOutInfo.checkOut}\n`;
        }
        if (checkInOutInfo.checkOutText) {
          marketingText += `${checkInOutInfo.checkOutText}\n`;
        }
        marketingText += `\n`;
      }
    }
    
    // Add booking details
    marketingText += `📅 *פרטי הזמנה:*\n`;
    if (boardTypesDisplay) {
      marketingText += `בסיס אירוח: ${boardTypesDisplay}\n`;
    }
    if (numberOfNights) {
      marketingText += `${numberOfNights} לילות\n`;
    }
    if (dates && dates !== "__/__/____ - __/__/____") {
      marketingText += `תאריכים: ${dates}\n`;
    }
    if (totalRooms) {
      marketingText += `${totalRooms} חדרים\n`;
    }
    marketingText += `\n`;

    // Get room information
    if (roomsAndPaxInfo && roomsAndPaxInfo.length > 0) {
      marketingText += `🛏️ *פרטי חדרים:*\n`;
      roomsAndPaxInfo.forEach((roomInfo, index) => {
        const room = roomInfo?.room;
        const roomName = room?.roomType || room?.roomSet || `חדר ${index + 1}`;
        const adultsCount = roomInfo?.adults?.length || 0;
        const kidsCount = roomInfo?.kids?.length || 0;
        const infantsCount = roomInfo?.infants?.length || 0;

        marketingText += `\n*חדר ${index + 1}:* ${roomName}\n`;

        let occupancy = "";
        if (adultsCount > 0) {
          occupancy = `${adultsCount} מבוגרים`;
          if (kidsCount > 0) {
            occupancy += `, ${kidsCount} ילדים`;
          }
          if (infantsCount > 0) {
            occupancy += `, ${infantsCount} תינוקות`;
          }
        }
        if (occupancy) {
          marketingText += `${occupancy}\n`;
        }
      });
      marketingText += `\n`;
    }

    marketingText += `💰 *מחיר: ${priceStr}*\n\n`;

    // Add payment options
    marketingText += `💳 *אפשרויות תשלום:*\n`;
    marketingText += `• עד 3 תשלומים ללא ריבית\n`;
    marketingText += `• עד 12 תשלומים\n`;
    marketingText += `• ניתן לשלם בהעברה בנקאית\n\n`;

    if (hotelLink) {
      marketingText += `🔗 *קישור להזמנה:*\n${hotelLink}\n\n`;
    }
    
    marketingText += `⭐ הזדמנות שלא תחזור!\n\n`;

    // Add agent information
    marketingText += `👤 *לפרטים נוספים:*\n`;
    if (agentName) {
      marketingText += `${agentName}\n`;
    }
    if (agentPhone) {
      marketingText += `${agentPhone}\n`;
    }
    if (!agentName && !agentPhone) {
      marketingText += `צרו קשר איתי\n`;
    }

    return marketingText;
  } catch (error) {
    console.error("Error generating hotel marketing text:", error);
    return null;
  }
};

/**
 * Generate marketing text for organized tours
 */
export const generateTourMarketingText = ({
  selectedResult,
  roomsAndPaxInfo,
  ourPrice,
  ourSavedLDSOrder,
  agentData,
  airlines,
  t,
  /** כשקיים — מחיר לחומר לקוח (התאמת אדמין / הצעה) במקום שדות ourPrice בסיסיים */
  displayPriceIls = null,
}) => {
  if (!selectedResult) {
    return null;
  }

  try {
    const tour = selectedResult?.tour || {};
    const tourName = tour?.tourName || tour?.name || "טיול מאורגן";
    const { depDate, retDate } = get_depret_from_result(selectedResult);

    // Get price
    const totalPrice =
      displayPriceIls != null && Number.isFinite(Number(displayPriceIls))
        ? Number(displayPriceIls)
        : ourPrice?.priceWithCreditCardFee ||
          ourPrice?.priceWithHandleFee ||
          ourPrice?.priceWithCommission ||
          0;
    const priceStr =
      displayPriceIls != null && Number.isFinite(Number(displayPriceIls))
        ? formatPackageMarketingTextPriceIls(totalPrice)
        : convertPrice(totalPrice);

    // Get link if available
    const tourLink = ourSavedLDSOrder ? window.location.href : "";

    // Get agent information
    const userInfoFromStorage = getUserInfo();
    const agent = agentData || ourSavedLDSOrder?.agent || userInfoFromStorage || null;

    let agentName = "";
    if (agent) {
      const firstName = agent.firstname || agent.firstName || agent.FirstName || "";
      const lastName = agent.lastname || agent.lastName || agent.LastName || "";
      agentName = `${firstName} ${lastName}`.trim();
      if (!agentName) {
        agentName = agent.name || agent.Name || "";
      }
    }

    const agentPhone =
      agent?.cell ||
      agent?.Cell ||
      agent?.phone ||
      agent?.Phone ||
      agent?.mobile ||
      agent?.Mobile ||
      agent?.telephone ||
      agent?.Telephone ||
      "";

    // Build marketing text
    let marketingText = `✈️ *${tourName}*\n\n`;
    if (depDate && retDate) {
      marketingText += `📅 תאריכים: ${depDate} - ${retDate}\n`;
    }

    // Get flight information
    const flightsData = selectedResult?.linkedFlights?.flight;
    if (flightsData && airlines) {
      const flightsArray = Array.isArray(flightsData) ? flightsData : [flightsData];
      if (flightsArray.length > 0) {
        marketingText += `\n✈️ *פרטי טיסות:*\n`;
        flightsArray.forEach((flightItem, idx) => {
          const leg = Array.isArray(flightItem?.leg) ? flightItem.leg[0] : flightItem?.leg;
          if (leg) {
            const flightType = idx === 0 ? "טיסת הלוך" : "טיסת חזרה";
            const airlineId =
              leg.airlineId || leg.airline?.id || leg.airline?.code || leg.airlineCode || "";
            let airline = null;
            if (airlineId && airlines) {
              airline = airlines.find(
                (a) =>
                  a.id?.toString() === airlineId.toString() ||
                  a.code?.toLowerCase() === airlineId.toString().toLowerCase()
              );
            }
            if (!airline && leg.flightNumber && airlines) {
              const flightNumberCode = leg.flightNumber.substring(0, 2).toUpperCase();
              airline = airlines.find((a) => a.code?.toUpperCase() === flightNumberCode);
            }
            const airlineCode = airline?.code || airlineId || "";
            const airlineName = airline?.name || airlineCode;
            const flightNumber = leg.flightNumber || "";
            const depTime = leg.depTime || "";
            const arrTime = leg.arrTime || "";
            const availableSeats =
              leg.availableSeats || leg.seatsAvailable || leg.seats || "";

            const depCityCode = leg.depCity || leg.depAirport?.code || "";
            const arrCityCode = leg.arrCity || leg.arrAirport?.code || "";
            const depCityTranslated = translateLDSDestinations(depCityCode);
            const arrCityTranslated = translateLDSDestinations(arrCityCode);
            const depCityName = depCityTranslated?.CITYNAME_HEB || depCityCode;
            const arrCityName = arrCityTranslated?.CITYNAME_HEB || arrCityCode;

            marketingText += `\n*${flightType}:*\n`;
            marketingText += `${depCityName} → ${arrCityName}\n`;
            if (airlineName) {
              marketingText += `חברת תעופה: ${airlineName}\n`;
            }
            if (flightNumber) {
              marketingText += `מספר טיסה: ${airlineCode} ${flightNumber}\n`;
            }
            if (depTime) {
              marketingText += `המראה: ${depTime}\n`;
            }
            if (arrTime) {
              marketingText += `נחיתה: ${arrTime}\n`;
            }
            if (availableSeats) {
              marketingText += `מקומות זמינים: ${availableSeats}\n`;
            }
          }
        });
      }
    }

    // Get room information
    if (roomsAndPaxInfo && roomsAndPaxInfo.length > 0) {
      marketingText += `\n🛏️ *חדרים:*\n`;
      roomsAndPaxInfo.forEach((roomInfo, index) => {
        const room = roomInfo?.room;
        const roomName = room?.roomType || room?.roomSet || `חדר ${index + 1}`;
        const adultsCount = roomInfo?.adults?.length || 0;
        const kidsCount = roomInfo?.kids?.length || 0;
        const infantsCount = roomInfo?.infants?.length || 0;

        marketingText += `\n*חדר ${index + 1}:* ${roomName}\n`;

        let occupancy = "";
        if (adultsCount > 0) {
          occupancy = `${adultsCount} מבוגרים`;
          if (kidsCount > 0) {
            occupancy += `, ${kidsCount} ילדים`;
          }
          if (infantsCount > 0) {
            occupancy += `, ${infantsCount} תינוקות`;
          }
        }
        if (occupancy) {
          marketingText += `${occupancy}\n`;
        }
      });
    }

    marketingText += `\n💰 *מחיר: ${priceStr}*\n`;

    // Add payment options
    marketingText += `\n💳 *אפשרויות תשלום:*\n`;
    marketingText += `• עד 3 תשלומים ללא ריבית\n`;
    marketingText += `• עד 12 תשלומים\n`;
    marketingText += `• ניתן לשלם בהעברה בנקאית\n`;

    if (tourLink) {
      marketingText += `\n🔗 קישור להזמנה:\n${tourLink}\n`;
    }
    marketingText += `\n⭐ הזדמנות שלא תחזור!\n`;

    // Add agent information
    marketingText += `\n👤 *לפרטים נוספים:*\n`;
    if (agentName) {
      marketingText += `${agentName}\n`;
    }
    if (agentPhone) {
      marketingText += `${agentPhone}\n`;
    }
    if (!agentName && !agentPhone) {
      marketingText += `צרו קשר איתי\n`;
    }

    return marketingText;
  } catch (error) {
    console.error("Error generating tour marketing text:", error);
    return null;
  }
};

/**
 * Copy marketing text to clipboard
 */
export const copyMarketingTextToClipboard = (marketingText, message) => {
  if (!marketingText) {
    message.error("חסרים נתונים ליצירת מלל שיווקי");
    return;
  }

  navigator.clipboard
    .writeText(marketingText)
    .then(() => {
      message.success("המלל השיווקי הועתק ללוח! ניתן להדביק בווטסאפ");
    })
    .catch((err) => {
      console.error("Error copying text:", err);
      message.error("שגיאה בהעתקת המלל");
    });
};

/**
 * Open WhatsApp with marketing text
 */
export const openWhatsAppWithMarketingText = (marketingText, message) => {
  if (!marketingText) {
    message.error("חסרים נתונים ליצירת מלל שיווקי");
    return;
  }

  // Copy to clipboard first
  navigator.clipboard
    .writeText(marketingText)
    .then(() => {
      // Open WhatsApp Web with the text
      const whatsappUrl = `https://web.whatsapp.com/send?text=${encodeURIComponent(marketingText)}`;
      window.open(whatsappUrl, "_blank");
      message.success("המלל הועתק וווטסאפ נפתח עם המלל מוכן!");
    })
    .catch((err) => {
      console.error("Error copying text:", err);
      message.error("שגיאה בהעתקת המלל");
    });
};

